import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { withBackend } from '../utils/api';
import { LoginPage } from './LoginPage';

// Parse the redeem code out of /redeem/<code> (and /ar/redeem/<code>).
function getCodeFromPath() {
  const path = window.location.pathname.replace(/^\/ar(?=\/)/, '');
  const after = path.split('/redeem/')[1] || '';
  return after.split(/[/?#]/)[0].trim().toLowerCase();
}

const ERROR_COPY = {
  invalid_code: 'This link is not valid. Please check it or ask us for a new one.',
  link_expired: 'This invitation link has expired. Reach out and we will send a fresh one.',
  already_redeemed: 'This link has already been used. If you need access, let us know and we will sort it out.',
  already_on_trial: 'You already have an active trial running — no need to redeem again.',
  redeem_failed: 'Something went wrong activating your trial. Please try again in a moment.',
};

/**
 * Landing page for a single-use trial link.
 * - Stashes the code so it survives the signup -> auto-sign-in bounce.
 * - If signed out, shows the trial intro + the normal login/signup page.
 * - Once signed in, claims the code and shows the activation result.
 */
export default function RedeemTrialPage({ currentUser, onLogin, onRedeemed }) {
  const code = useMemo(getCodeFromPath, []);
  const [status, setStatus] = useState('idle'); // idle | working | success | error
  const [message, setMessage] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState(null);

  // Persist the code through the auth bounce. Uses localStorage (not session)
  // because email-confirmation opens a FRESH tab, which has its own
  // sessionStorage; localStorage is shared across tabs of the same origin so
  // the code survives the signup -> confirm-email -> new-tab journey.
  useEffect(() => {
    if (code) {
      try { localStorage.setItem('pending_trial_code', code); } catch {}
    }
  }, [code]);

  // Once we have an authenticated user, claim the trial.
  useEffect(() => {
    if (!currentUser?.id || !code || status !== 'idle') return;
    setStatus('working');
    (async () => {
      try {
        const resp = await fetch(withBackend('/api/trial/redeem'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: currentUser.id, code }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.ok && data.success) {
          try { localStorage.removeItem('pending_trial_code'); } catch {}
          setTrialEndsAt(data.trial_ends_at || null);
          setStatus('success');
          onRedeemed?.(data);
        } else {
          // Terminal error (bad/used/expired code) — drop it so it can't retry.
          try { localStorage.removeItem('pending_trial_code'); } catch {}
          setStatus('error');
          setMessage(ERROR_COPY[data.error] || ERROR_COPY.redeem_failed);
        }
      } catch {
        setStatus('error');
        setMessage(ERROR_COPY.redeem_failed);
      }
    })();
  }, [currentUser, code, status, onRedeemed]);

  const goToApp = useCallback(() => { window.location.assign('/'); }, []);

  const endsLabel = useMemo(() => {
    if (!trialEndsAt) return null;
    try {
      return new Date(trialEndsAt).toLocaleDateString(undefined, {
        weekday: 'long', day: 'numeric', month: 'long',
      });
    } catch { return null; }
  }, [trialEndsAt]);

  // Invalid / missing code.
  if (!code) {
    return (
      <Centered>
        <h1 className="text-xl font-semibold text-slate-900">Invalid link</h1>
        <p className="mt-2 text-sm text-slate-600">{ERROR_COPY.invalid_code}</p>
      </Centered>
    );
  }

  // Not signed in: show the trial intro, then the normal auth page below it.
  // The code is already stashed, so it is claimed as soon as the user is in.
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-2xl px-6 pt-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
            7-day full trial
          </div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">You're invited to try Wizardly</h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
            Create your account below to activate 7 days of full access. Everything is unlimited;
            the only limit is that Opus decks are capped at 16 slides.
          </p>
        </div>
        <LoginPage onLogin={onLogin} />
      </div>
    );
  }

  // Signed in: activation states.
  return (
    <Centered>
      {status === 'working' && (
        <>
          <Spinner />
          <p className="mt-4 text-sm text-slate-600">Activating your 7-day trial…</p>
        </>
      )}
      {status === 'success' && (
        <>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-2xl">✓</div>
          <h1 className="mt-4 text-xl font-semibold text-slate-900">Your trial is active</h1>
          <p className="mt-2 text-sm text-slate-600">
            Full access{endsLabel ? ` until ${endsLabel}` : ' for the next 7 days'}. Opus decks are capped at 16 slides.
          </p>
          <button
            onClick={goToApp}
            className="mt-6 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700"
          >
            Start creating
          </button>
        </>
      )}
      {status === 'error' && (
        <>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-2xl">!</div>
          <h1 className="mt-4 text-xl font-semibold text-slate-900">Couldn't activate the trial</h1>
          <p className="mt-2 text-sm text-slate-600">{message}</p>
          <button
            onClick={goToApp}
            className="mt-6 rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Continue to Wizardly
          </button>
        </>
      )}
    </Centered>
  );
}

function Centered({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-200">
        {children}
      </div>
    </div>
  );
}

function Spinner() {
  return <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-violet-200 border-t-violet-600" />;
}
